import { FC } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import formatDistanceToNow from 'date-fns/formatDistanceToNow'
import { constants } from 'ethers'

import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import MuiTableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import MuiTableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import Back from '@mui/icons-material/ArrowBack'
import Telegram from '@mui/icons-material/Telegram'

import useDApp from 'contexts/Web3'

import Header from 'containers/Header'

import { Knight as KnightIcon, Trophy as TrophyIcon } from 'components/Icons/PngIcon'
import Help from 'components/Help'

import { LinkHome } from 'routes'
import shortenAccount from 'utils/shortenAccount'

import useTokenId from './useTokenId'

import './round-table-knights.scss'
/* eslint-disable-next-line import/no-webpack-loader-syntax */
import imgHelp from '!!file-loader!assets/help-knights.png'

const telegram = <Telegram color="inherit" />

const TableRow = styled(MuiTableRow)({
  '&:last-child .MuiTableCell-root': {
    borderBottomStyle: 'none',
  }
})

const TableCell = styled(MuiTableCell)({
  paddingLeft: 0,
  paddingRight: 0,
  borderBottomColor: '#9B9B9B',

  '&:first-child': {
    paddingLeft: 8,
  },
  '&:last-child': {
    paddingRight: 8,
  },
})

const QueenIcon = <TrophyIcon color="warning" sx={{ width: 24, height: 24, filter: `drop-shadow(0 .4px 0 #0009)` }} />

const SeatOwner = styled(Chip)({
  background: '#FFF',
  borderColor: '#9B9B9B',
  color: '#683100',
  '& .MuiChip-label': {
    minWidth: '10em',
    textAlign: 'center',
  },
})

const RoundTableKnights: FC = () => {
  const {
    knight: { list, queenId },
    // wallet: { account },
    configs: {
      links,
    },
  } = useDApp()

  const tokenId = useTokenId()
  const nav = useNavigate()

  return (
    <Container className="knights-view" maxWidth="sm" disableGutters>
      <Stack direction="row" alignItems="center" p={1}>
        <LinkHome>
          <Typography variant="HeaderUI" flexGrow={0}>
            <IconButton color="inherit">
              <Back color="inherit" fontSize="large" />
            </IconButton>
          </Typography>
        </LinkHome>

        <Box flexGrow={1} />
        <Header />
        <Box flexGrow={1} />

        <Typography variant="HeaderUI" flexGrow={0}>
          <Help image={imgHelp} />
        </Typography>
      </Stack>

      <Box height={140} />

      <Box p={2} width="100%">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="35%" align="left">
                  <Typography variant="KnightsTableHeader">
                    Player
                  </Typography>
                </TableCell>
                <TableCell width="5%" />
                <TableCell width="30%" align="right">
                  <Typography variant="KnightsTableHeader">
                    Power
                  </Typography>
                </TableCell>
                <TableCell width="30%" align="right">
                  <Typography variant="KnightsTableHeader">
                    Profits
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {list.map(({ id, owner, since, amount, profit }) => (
                <TableRow
                  key={id}
                  selected={id === tokenId}
                  hover
                  onClick={() => nav(`${id}`)}
                >
                  <TableCell component="th" scope="row">
                    <SeatOwner
                      variant="outlined"
                      avatar={<KnightIcon tokenId={id} />}
                      label={owner ? shortenAccount(owner) : 'Empty'}
                    />
                  </TableCell>
                  <TableCell>
                    {id === queenId ? QueenIcon : null }
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="KnightsTableCell">
                      {
                        owner
                          ? (amount ? amount.toFixed(1) : '0.0')
                          : '- -'
                      }
                    </Typography>
                    <Typography
                      color="info"
                      fontSize="smaller"
                      component="p"
                      textAlign="right"
                    >
                      {
                        // 佔領時間
                        owner && since
                          ? formatDistanceToNow(since * 1000)
                          : ''
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="KnightsTableCell"
                      component="p"
                    >
                      {profit.toFixed(2)} TT
                    </Typography>

                    {owner && amount && since ? (
                      <Typography
                        fontSize="smaller"
                        fontWeight={600}
                        component="p"
                        color={profit > 0 ? 'success.main' : 'error.main'}
                      >
                        APR{' '}
                        {(
                          (profit * 86400 * 365 * 1e5) /
                          (amount * (Date.now() - since * 1000))
                        ).toFixed(1)}
                        %
                      </Typography>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box width="100%" textAlign="center" mb={2}>
        <Button
          endIcon={telegram}
          variant="ugly"
          color="primary"
          href={links.telegram}
        >
          Join Telegram
        </Button>
        <Typography
          variant="UglyDialogInnerHint"
          component="p"
          mt={1}
        >
          Receive Knight's notifications from Telegram
        </Typography>
      </Box>

      <Outlet />
    </Container>
  )
}

export default RoundTableKnights
